import React, { createContext, useContext, useState, useEffect } from 'react';

// Create a context to manage cart state across the application.
const CartContext = createContext();

/**
 * CartProvider wraps children in a context provider. It exposes
 * cart state and helper functions for adding, updating and removing
 * items. Quantity is always at least one.
 */
export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    // Initialize from localStorage if available
    const savedCart = localStorage.getItem('bmw-plug-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        return parsedCart && parsedCart.items ? parsedCart : { items: [] };
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        return { items: [] };
      }
    }
    return { items: [] };
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart && cart.items && cart.items.length > 0) {
      localStorage.setItem('bmw-plug-cart', JSON.stringify(cart));
    } else {
      // If cart is empty, remove from localStorage
      localStorage.removeItem('bmw-plug-cart');
    }
  }, [cart]);

  // Calculate total whenever cart changes
  const total = cart && cart.items ? cart.items.reduce((sum, item) => {
    return sum + (item.price * item.quantity);
  }, 0) : 0;

  const addToCart = (product, quantity = 1) => {
    console.log('Adding to cart:', product, 'quantity:', quantity);
    setCart((prevCart) => {
      // Ensure prevCart has the right structure
      const currentCart = prevCart && prevCart.items ? prevCart : { items: [] };
      const existingItem = currentCart.items.find((item) => item.id === product.id);

      if (existingItem) {
        const updatedCart = {
          ...currentCart,
          items: currentCart.items.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
          ),
        };
        console.log('Updated cart (existing item):', updatedCart);
        return updatedCart;
      } else {
        const updatedCart = {
          ...currentCart,
          items: [...currentCart.items, { ...product, quantity }],
        };
        console.log('Updated cart (new item):', updatedCart);
        return updatedCart;
      }
    });
  };

  const removeFromCart = (productId) => {
    console.log('Removing from cart:', productId);
    setCart((prevCart) => {
      const currentCart = prevCart && prevCart.items ? prevCart : { items: [] };
      const updatedCart = {
        ...currentCart,
        items: currentCart.items.filter((item) => item.id !== productId),
      };
      console.log('Updated cart after removal:', updatedCart);
      return updatedCart;
    });
  };

  const updateQuantity = (productId, quantity) => {
    console.log('Updating quantity:', productId, 'to:', quantity);
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart((prevCart) => {
      const currentCart = prevCart && prevCart.items ? prevCart : { items: [] };
      const updatedCart = {
        ...currentCart,
        items: currentCart.items.map((item) => 
          item.id === productId ? { ...item, quantity } : item
        ),
      };
      console.log('Updated cart with new quantity:', updatedCart);
      return updatedCart;
    });
  };

  const clearCart = () => {
    console.log('Clearing cart');
    setCart({ items: [] });
  };

  const getItemCount = () => {
    return cart && cart.items ? cart.items.reduce((sum, item) => sum + item.quantity, 0) : 0;
  };

  // Debug logging
  useEffect(() => {
    console.log('Cart updated:', cart);
    console.log('Total items:', getItemCount());
    console.log('Total price:', total);
  }, [cart, total]);

  return (
    <CartContext.Provider
      value={{
        cart,
        total,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

/**
 * Custom hook to read cart context in other components.
 */
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}